/* @flow */
import { Observable } from 'rxjs';
import { combineEpics } from 'redux-observable';
import { fromJS } from 'immutable';
import isString from 'lodash.isstring';
import { toLower } from 'trustar/utils';
import { prop } from 'ramda';
import { v4 as uuid } from 'uuid';
import { readFile } from 'trustar/utils/utils.js';

import createDebugger from 'debug';
const debug = createDebugger('app:modules:redaction:epics'); // eslint-disable-line no-unused-vars

import {
  REDACTABLE_ENDPOINT,
  FETCH_LIBRARY_ENDPOINT,
  ADD_ENDPOINT,
  DELETE_ENDPOINT,
  categoryByName,
} from './constants.js';
import {
  SAVE,
  SAVE_FAILURE,
  UPLOAD_REDACTION_FILE,
  BULK_SAVE,
  BULK_SAVE_SUCCESS,
  BULK_ADD_TERMS,
  BULK_SAVE_FAILURE,
  DELETE,
  DELETE_SUCCESS,
  DELETE_FAILURE,
  FETCH_SHARED_LIBRARY,
  FETCH_SUCCESS,
  FETCH_REDACTABLES,
  FETCH_REDACTABLES_SUCCESS,
  FETCH_REDACTABLES_FAILURE,
} from './action-types.js';
import { FETCH_SUCCESS as FETCH_PROFILE_SUCCESS } from 'modules/profile/action-types.js';
import { redactionMap, redactionTermExists } from './getters.js';
import { mapRedactables, validateTerm } from './utils.js';
import { success, info, error, warn } from 'modules/alerts/actions.js';

/* Handle fetching shared library terms */
const fetchSharedLibraryEpic = (action$, store, { ajax }) =>
  action$.ofType(FETCH_SHARED_LIBRARY, FETCH_PROFILE_SUCCESS)
    .mergeMap(() => {
      /* Fetch library and save terms to store */
      return ajax.get(FETCH_LIBRARY_ENDPOINT)
        .mergeMap(({ response }) => {
          return [{ type: FETCH_SUCCESS, payload: response }];
        });
    }).catch(err => {
      return Observable.of(error('There was an error fetching the redaction library.'));
    });

/* Handle shared */
const addTermEpic = (action$, store, { ajax }) =>
  action$.ofType(SAVE)
    .mergeMap(({ payload: term }) => {
      const errorMessage = validateTerm(
        term,
        redactionTermExists(term.text.trim())(store.getState())
      );

      if (errorMessage) {
        return Observable.of(error(errorMessage));
      }

      term.id = uuid(); // Add an id

      return ajax.postJSON(ADD_ENDPOINT, [ term ])
        .mergeMap(({ response }) => {
          const { savedTerms, failedTerms } = response;
          let action = [
            { type: FETCH_SUCCESS, payload: savedTerms }
          ];
          if (failedTerms.length > 0) {
            action.push(warn('The term could not be added because it already exists.'));
          }
          return action;
        });
    })
    .catch(err => {
      return [
        { type: SAVE_FAILURE },
        error('There was an error adding the redaction term to the shared library.')
      ];
    });


/* Handle shared */
const deleteTermEpic = (action$, store, { ajax }) =>
  action$.ofType(DELETE)
    .mergeMap(({ payload: term }) => {
      return ajax.delete(`${DELETE_ENDPOINT}/${term.get('id')}`)
        .mergeMap(() => {
          return [{
            type: DELETE_SUCCESS,
            payload: term
          }];
        })
        .catch(err => {
          return [
            { type: DELETE_FAILURE },
            error('There was an error removing the redaction term from the shared library.')
          ];
        });
    });

/* Fetch all the redactable terms from the server, and alert the user to how
 * many terms we found. */
const fetchRedactablesEpic = (action$, store, { ajax }) =>
  action$.ofType(FETCH_REDACTABLES)
    .mergeMap(({ payload }) => {
      const data = `${payload.getIn(['content', 'data'], '')}\n${payload.get('title')}`;
      return ajax.postJSON(REDACTABLE_ENDPOINT, data);
    })
    .map(prop('response'))
    .mergeMap(response => {

      const redacted = redactionMap(store.getState()).map(x => toLower(x.get('text')));
      const redactables = fromJS(mapRedactables(response).filter(x => {
        const text = toLower(x.get('text'));
        const categoryId = x.get('categoryId');
        return !redacted.find(y => {
          return categoryId === 12 ? text === y : text.includes(y);
        });
      }));

      return Observable.of({
        type: FETCH_REDACTABLES_SUCCESS,
        payload: redactables
      });

    }).catch(err => {
      return [
        error('There was an error fetching redactable terms.'),
        { type: FETCH_REDACTABLES_FAILURE }
      ];
    });

/* Top level epic for importing terms from file */
const importTermsEpic = (action$, store) =>
  action$.ofType(UPLOAD_REDACTION_FILE)
  .mergeMap(({ payload: files }) => {
    const [file] = files;
    const type = file.type;
    return Observable.fromPromise(readFile(file))
    .mergeMap((contents: string) => {
      let omittedTerms = 0;
      let otherTerms = 0;
      let parsedTerms = [];

      switch (type) {
      case 'text/csv':
        // $FlowIgnore
        const parsedContent = contents.replaceAll('\b', '');
        parsedTerms = fromJS(parsedContent.split(/\r?\n|\r/))
        .map(term => {
          const [ text, category ] = term.split(/\,/);
          return fromJS({
            text,
            category,
          });
        });
        break;
      case 'application/json':
      case 'text/plain':
      default:
        parsedTerms = fromJS(JSON.parse(contents));
        break;
      }

      const terms = parsedTerms.filter(term => {
        const text = term.get('text');

        // Empty string at the end of file was parsed
        if (!text) return false;

        // Term text to short
        if (text.trim().length < 2) {
          omittedTerms++;
          return false;
        }

        // Term text already exists
        if (redactionTermExists(text)(store.getState())) {
          omittedTerms++;
          return false;
        }

        return true;
      })
      .map((term, k) => {
        let categoryId = 17; // Set the categoryId as 17 (Other) as default
        if (term.has('categoryId')) {
          categoryId = term.get('categoryId');
          if (categoryId < 1 || categoryId > 20) { // If categoryId is out of valid range, assign to Other
            otherTerms++;
          }
        } else if (term.has('category') && isString(term.get('category'))) {
          const category = categoryByName(term.get('category'));
          if (!category) { // If category is not found for name, assign to Other
            otherTerms++;
          } else {
            categoryId = category.get('id');
          }
        } else { // If term has no categoryId or category field, assign to Other
          otherTerms++;
        }

        return fromJS({
          id: uuid(),
          type: 'TEXT',
          text: term.get('text'),
          categoryId,
        });
      })
      .toMap()
      .mapKeys((k, v) => v.get('id'));

      let action = [{
        type: BULK_ADD_TERMS,
        payload: terms
      }];

      // Warn of omitted terms
      if (omittedTerms) {
        const pluralizedMessage = omittedTerms > 1
          ? `${omittedTerms} terms were omitted because they already existed or were less than 2 characters.`
          : `${omittedTerms} term was omitted because it already existed or was less than 2 characters.`;
        action.push(warn(pluralizedMessage));
      }

      // Warn of terms assigned to other due to missing/invalid category information
      if (otherTerms) {
        const otherMessage = otherTerms > 1
          ? `${otherTerms} terms were added to Other because they had invalid categories.`
          : `${otherTerms} term was added to Other because the category was invalid.`;
        action.push(warn(otherMessage));
      }

      if (terms.size > 0 && terms.size <= 10000) {
        action.push({ type: BULK_SAVE, payload: terms });
      } else if (terms.size > 10000) {
        action.push(error('The number of terms exceeds 10,000. Please limit bulk imports to 10,000 terms or less.'));
      }

      /* All the terms were ommitted */
      if (terms.size === 0) {
        action.push({ type: BULK_SAVE_FAILURE });
      }

      return action;

    })
    .catch(err => {
      return Observable.from([
        error('There was an error importing the file. Please verify that it is a valid .json or .csv file.'),
        { type: BULK_SAVE_FAILURE, payload: err }
      ]);
    });
  });

/* Handle shared */
const bulkSaveTermsEpic = (action$, store, { ajax }) =>
  action$.ofType(BULK_SAVE)
    .mergeMap(({ payload: terms }) => {
      /* Send to server. In response, handle any possible duplicated terms with a
       * warning message to the user  */
      const body = terms.toList().toJS();
      return ajax.postJSON(ADD_ENDPOINT, body)
        .mergeMap(({ response }) => {
          const { savedTerms, failedTerms } = response;
          let action = [
            { type: BULK_SAVE_SUCCESS },
            { type: FETCH_SUCCESS, payload: savedTerms },
            success(`${body.length - failedTerms.length} terms successfully imported.`)
          ];
          if (failedTerms.length) {
            action.push(info(`${failedTerms.length} terms were not added because they already existed in the library.`));
          }
          return action;
        });
    }).catch(err => {
      return [
        error('There was an error saving bulk terms.'),
        { type: BULK_SAVE_FAILURE, payload: err }
      ];
    });

export default combineEpics(
  fetchSharedLibraryEpic,
  addTermEpic,
  deleteTermEpic,
  fetchRedactablesEpic,
  importTermsEpic,
  bulkSaveTermsEpic,
);



// WEBPACK FOOTER //
// ./src/app/modules/redaction/epics.js